import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';

import {
  SkillDocumentResponse,
  SkillGroup,
  SkillLevelStructureUpdateRequest,
  SkillLevelType,
  SkillSpecializationResponse,
  SkillTestQuestion,
  TheoryLevelResponse,
} from '../../../models/skill-learning.model';
import {
  normalizeConfiguredSkillLevelCount,
  resolveSkillLevelType,
  SKILL_LEARNING_EMPTY_TEXT,
  SKILL_LEARNING_FIRST_INDEX,
  SKILL_LEARNING_LEVEL_COUNT,
  SKILL_LEARNING_LEVEL_TAB,
  SKILL_LEVEL_TYPE,
  SKILL_STRUCTURE_MESSAGE,
  SKILL_VALUE_TYPE,
  SkillLearningLevelTab,
} from '../../../options/skill-learning.option';
import {
  TheoryLevelSavePayload,
  TheoryTestGenerationPayload,
} from '../theory-level-editor/theory-level-editor.option';
import { PracticeLevelSavePayload } from '../practice-level-editor/practice-level-editor.component';

// TypeScript 2.3 needs local aliases to emit safe metadata for nullable interface inputs.
type NullableSpecialization = SkillSpecializationResponse | null;
type NullableDocument = SkillDocumentResponse | null;

@Component({
  selector: 'app-edit-specialization-levels-page',
  templateUrl: './edit-specialization-levels-page.component.html',
  styleUrls: ['./edit-specialization-levels-page.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditSpecializationLevelsPageComponent {

  readonly levelTabs = SKILL_LEARNING_LEVEL_TAB;
  readonly levelCounts = SKILL_LEARNING_LEVEL_COUNT;
  readonly levelTypes = SKILL_LEVEL_TYPE;
  readonly structureMessages = SKILL_STRUCTURE_MESSAGE;

  @Input() specialization: NullableSpecialization = null;
  @Input() availableGroups: SkillGroup[] = [];
  @Input() selectedDepartmentId = SKILL_LEARNING_EMPTY_TEXT;
  @Input() structureErrorKey = SKILL_LEARNING_EMPTY_TEXT;
  @Input() theoryDirectionsPrompt = SKILL_LEARNING_EMPTY_TEXT;
  @Input() generatedTheoryTestQuestions: SkillTestQuestion[] = [];
  @Input() isGeneratingTheoryTests = false;
  @Input() practice2Document: NullableDocument = null;
  @Input() practice2DocumentFileId: string | null = null;
  @Input() practice3Document: NullableDocument = null;
  @Input() practice3DocumentFileId: string | null = null;
  @Input() isSubmitting = false;
  @Input() activeTab: SkillLearningLevelTab = SKILL_LEARNING_LEVEL_TAB.LEVEL_1;

  @Output() activeTabChanged = new EventEmitter<SkillLearningLevelTab>();
  @Output() departmentChanged = new EventEmitter<string>();
  @Output() structureChanged = new EventEmitter<SkillLevelStructureUpdateRequest>();
  @Output() requirementFocusRequested = new EventEmitter<void>();
  @Output() theoryLevelSaved = new EventEmitter<TheoryLevelSavePayload>();
  @Output() theoryTestQuestionsGenerationRequested = new EventEmitter<TheoryTestGenerationPayload>();
  @Output() practiceLevel2Saved = new EventEmitter<PracticeLevelSavePayload>();
  @Output() practiceLevel3Saved = new EventEmitter<PracticeLevelSavePayload>();
  @Output() documentDownloadRequested = new EventEmitter<number>();
  @Output() documentPreviewRequested = new EventEmitter<number>();

  confirmationMessageKey = SKILL_LEARNING_EMPTY_TEXT;
  confirmationLevel = SKILL_LEARNING_LEVEL_COUNT.ONE;
  pendingStructureRequest: SkillLevelStructureUpdateRequest | null = null;
  private structureConfirmationTrigger: HTMLElement | null = null;

  get configuredLevelCount(): number {
    return normalizeConfiguredSkillLevelCount(
      this.specialization && this.specialization.configuredLevelCount,
    );
  }

  get level2Type(): SkillLevelType {
    return resolveSkillLevelType(
      this.levelCounts.TWO,
      this.specialization ? this.specialization.configuredLevel2Type : null,
      this.specialization ? this.specialization.configuredLevel3Type : null,
    );
  }

  get level3Type(): SkillLevelType {
    return resolveSkillLevelType(
      this.levelCounts.THREE,
      this.specialization ? this.specialization.configuredLevel2Type : null,
      this.specialization ? this.specialization.configuredLevel3Type : null,
    );
  }

  get configuredSequence(): SkillLevelType[] {
    return this.buildSequence(this.configuredLevelCount, this.level2Type, this.level3Type);
  }

  get publishedSequence(): SkillLevelType[] {
    if (!this.specialization || !this.specialization.publishedLevelCount) {
      return [];
    }
    return this.buildSequence(
      this.specialization.publishedLevelCount,
      resolveSkillLevelType(this.levelCounts.TWO, this.specialization.publishedLevel2Type, null),
      resolveSkillLevelType(this.levelCounts.THREE, null, this.specialization.publishedLevel3Type),
    );
  }

  get allowedStructureGroups(): SkillGroup[] {
    const ids = this.specialization && this.specialization.allowedStructureDepartmentIds || [];
    return ids.map(id => this.availableGroups.find(group => group.id === id) || { id, name: id });
  }

  get needsDepartmentSelection(): boolean {
    return this.allowedStructureGroups.length > this.levelCounts.ONE && !this.selectedDepartmentId;
  }

  get canChangeStructure(): boolean {
    if (!this.specialization || !this.specialization.canChangeLevelStructure) {
      return false;
    }
    if (typeof this.specialization.configurationRevision !== SKILL_VALUE_TYPE.NUMBER) {
      return false;
    }
    const ids = this.specialization.allowedStructureDepartmentIds || [];
    return !ids.length || ids.indexOf(this.selectedDepartmentId) >= SKILL_LEARNING_FIRST_INDEX;
  }

  get structureDisabledReasonKey(): string {
    if (!this.specialization || this.canChangeStructure) {
      return SKILL_LEARNING_EMPTY_TEXT;
    }
    if (!this.specialization.canChangeLevelStructure) {
      return SKILL_STRUCTURE_MESSAGE.DENIED;
    }
    if (typeof this.specialization.configurationRevision !== SKILL_VALUE_TYPE.NUMBER) {
      return SKILL_STRUCTURE_MESSAGE.REVISION_REQUIRED;
    }
    return this.needsDepartmentSelection
      ? SKILL_STRUCTURE_MESSAGE.CONTEXT_DENIED
      : SKILL_STRUCTURE_MESSAGE.DENIED;
  }

  selectTab(tab: SkillLearningLevelTab): void {
    if (this.activeTab === tab || !this.hasLevel(this.tabLevel(tab))) {
      return;
    }
    this.activeTab = tab;
    this.activeTabChanged.emit(tab);
  }

  onDepartmentChanged(departmentId: string): void {
    this.departmentChanged.emit(departmentId || SKILL_LEARNING_EMPTY_TEXT);
  }

  onAddLevel(): void {
    if (!this.canChangeStructure || this.configuredLevelCount >= this.levelCounts.THREE) {
      return;
    }
    const count = this.configuredLevelCount + this.levelCounts.ONE;
    this.emitStructure(count, this.level2Type, this.resolveAddedLevel3Type(count));
  }

  onRemoveLevel(trigger?: HTMLElement): void {
    if (!this.canRemoveLevel) {
      return;
    }
    const count = this.configuredLevelCount - this.levelCounts.ONE;
    const request = this.buildStructureRequest(
      count,
      count >= this.levelCounts.TWO ? this.level2Type : null,
      null,
    );
    const messageKey = this.hasContent(this.configuredLevelCount)
      ? SKILL_STRUCTURE_MESSAGE.REMOVE_CONFIRM : SKILL_LEARNING_EMPTY_TEXT;
    this.structureConfirmationTrigger = trigger || null;
    this.requestConfirmation(request, this.configuredLevelCount, messageKey);
  }

  onLevelTypeChanged(level: number, type: SkillLevelType, trigger?: HTMLElement): void {
    if (!this.canChangeStructure || !this.hasLevel(level) || type === this.levelType(level)) {
      return;
    }
    this.structureConfirmationTrigger = trigger || null;
    this.requestTypeChange(level, type);
  }

  get canRemoveLevel(): boolean {
    return this.canChangeStructure && this.configuredLevelCount > this.levelCounts.ONE;
  }

  hasLevel(level: number): boolean {
    return level <= this.configuredLevelCount;
  }

  levelType(level: number): SkillLevelType {
    return resolveSkillLevelType(level, this.level2Type, this.level3Type);
  }

  theoryLevel(level: number): TheoryLevelResponse | null {
    if (!this.specialization) {
      return null;
    }
    if (level === this.levelCounts.ONE) {
      return this.specialization.theoryLevel;
    }
    return level === this.levelCounts.TWO
      ? this.specialization.theoryLevel2 || null
      : this.specialization.theoryLevel3 || null;
  }

  onTheorySaved(payload: TheoryLevelSavePayload): void {
    this.theoryLevelSaved.emit(payload);
  }

  onTheoryGenerationRequested(payload: TheoryTestGenerationPayload): void {
    this.theoryTestQuestionsGenerationRequested.emit(payload);
  }

  onPractice2Saved(payload: PracticeLevelSavePayload): void {
    this.practiceLevel2Saved.emit(payload);
  }

  onPractice3Saved(payload: PracticeLevelSavePayload): void {
    this.practiceLevel3Saved.emit(payload);
  }

  requestDocument(level: number): void {
    this.documentDownloadRequested.emit(level);
  }

  trackByGroupId(index: number, group: SkillGroup): string {
    return group.id;
  }

  trackBySequenceIndex(index: number, type: SkillLevelType): number {
    return index;
  }

  previewDocument(level: number): void {
    this.documentPreviewRequested.emit(level);
  }

  focusRequirements(): void {
    this.requirementFocusRequested.emit();
  }

  confirmStructureChange(): void {
    if (this.pendingStructureRequest) {
      this.structureChanged.emit(this.pendingStructureRequest);
    }
    this.cancelStructureChange();
  }

  cancelStructureChange(): void {
    this.pendingStructureRequest = null;
    this.confirmationMessageKey = SKILL_LEARNING_EMPTY_TEXT;
    if (this.structureConfirmationTrigger) {
      this.structureConfirmationTrigger.focus();
      this.structureConfirmationTrigger = null;
    }
  }

  private requestTypeChange(level: number, type: SkillLevelType): void {
    const level2Type = level === this.levelCounts.TWO ? type : this.level2Type;
    const level3Type = level === this.levelCounts.THREE ? type
      : this.resolveLevel3AfterLevel2Change(level2Type);
    const request = this.buildStructureRequest(this.configuredLevelCount, level2Type, level3Type);
    const messageKey = this.typeConfirmationMessage(level, type);
    this.requestConfirmation(request, level, messageKey);
  }

  private emitStructure(
    count: number,
    level2Type: SkillLevelType | null,
    level3Type: SkillLevelType | null,
  ): void {
    this.structureChanged.emit(this.buildStructureRequest(count, level2Type, level3Type));
  }

  private buildStructureRequest(
    count: number,
    level2Type: SkillLevelType | null,
    level3Type: SkillLevelType | null,
  ): SkillLevelStructureUpdateRequest {
    const request: SkillLevelStructureUpdateRequest = {
      configuredLevelCount: count,
      expectedRevision: this.specialization.configurationRevision,
    };
    if (count >= this.levelCounts.TWO && level2Type) {
      request.level2Type = level2Type;
    }
    if (count >= this.levelCounts.THREE && level3Type) {
      request.level3Type = level3Type;
    }
    if (this.selectedDepartmentId) {
      request.departmentId = this.selectedDepartmentId;
    }
    return request;
  }

  private resolveAddedLevel3Type(count: number): SkillLevelType | null {
    return count >= this.levelCounts.THREE ? this.levelTypes.TASK : null;
  }

  private resolveLevel3AfterLevel2Change(level2Type: SkillLevelType): SkillLevelType | null {
    if (this.configuredLevelCount < this.levelCounts.THREE) {
      return null;
    }
    return level2Type === this.levelTypes.TASK ? this.levelTypes.TASK : this.level3Type;
  }

  private typeConfirmationMessage(level: number, type: SkillLevelType): string {
    const forcesLevel3 = level === this.levelCounts.TWO
      && type === this.levelTypes.TASK
      && this.configuredLevelCount === this.levelCounts.THREE
      && this.level3Type === this.levelTypes.TEST;
    if (!this.hasContent(level) && !forcesLevel3) {
      return SKILL_LEARNING_EMPTY_TEXT;
    }
    return forcesLevel3
      ? SKILL_STRUCTURE_MESSAGE.FORCE_LEVEL_THREE_CONFIRM
      : SKILL_STRUCTURE_MESSAGE.TYPE_CONFIRM;
  }

  private requestConfirmation(
    request: SkillLevelStructureUpdateRequest,
    level: number,
    messageKey: string,
  ): void {
    if (!messageKey) {
      this.structureConfirmationTrigger = null;
      this.structureChanged.emit(request);
      return;
    }
    this.pendingStructureRequest = request;
    this.confirmationLevel = level;
    this.confirmationMessageKey = messageKey;
  }

  private hasContent(level: number): boolean {
    const theory = this.theoryLevel(level);
    if (this.levelType(level) === this.levelTypes.TEST) {
      return !!theory && (!!theory.description || !!(theory.testQuestions || []).length);
    }
    const practice = level === this.levelCounts.TWO
      ? this.specialization.practiceLevel2 : this.specialization.practiceLevel3;
    return !!practice && (!!practice.description || !!practice.documentFileId);
  }

  private tabLevel(tab: SkillLearningLevelTab): number {
    return tab + this.levelCounts.ONE;
  }

  private buildSequence(
    count: number,
    level2Type: SkillLevelType,
    level3Type: SkillLevelType,
  ): SkillLevelType[] {
    const sequence: SkillLevelType[] = [this.levelTypes.TEST];
    if (count >= this.levelCounts.TWO) {
      sequence.push(level2Type);
    }
    if (count >= this.levelCounts.THREE) {
      sequence.push(level3Type);
    }
    return sequence;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/edit-specialization-levels-page/edit-specialization-levels-page.component.ts